import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useSettings,
  useUpdateSettings,
  useUpsertSchedule,
  useGenerateTelegramCode,
  useTelegramStatus,
} from "../api/queries.js";

/* ── helpers ──────────────────────────────────────────── */

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function formatEuro(cents: number) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(
    cents / 100,
  );
}

function parseTime(hhmm: string): { h: number; m: number } {
  const [h, m] = hhmm.split(":").map(Number);
  return { h: h ?? 19, m: m ?? 0 };
}

/* ── Icons (design line-icons, replace emoji) ─────────── */

type IconProps = { size?: number };
const sIcon = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24" as const,
  fill: "none" as const,
  stroke: "currentColor" as const,
  strokeWidth: 1.9,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
});

const EuroIcon = ({ size = 19 }: IconProps) => (
  <svg {...sIcon(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15.5 9.2a4 4 0 1 0 0 5.6M8.5 11h5.5M8.5 13.2h5.5" />
  </svg>
);
const ClockIcon = ({ size = 19 }: IconProps) => (
  <svg {...sIcon(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);
const CalendarIcon = ({ size = 19 }: IconProps) => (
  <svg {...sIcon(size)}>
    <rect x="3" y="4.5" width="18" height="16" rx="3" />
    <path d="M3 9h18M8 2.5v4M16 2.5v4" />
  </svg>
);
const CopyIcon = ({ size = 16 }: IconProps) => (
  <svg {...sIcon(size)}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);
const CheckIcon = ({ size = 22 }: IconProps) => (
  <svg {...sIcon(size)} strokeWidth={2.2}>
    <path d="M5 12l4 4 10-10" />
  </svg>
);
const PlaneIcon = ({ size = 19 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21.9 4.3l-3.2 15.1c-.2 1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-4.9 9-8.1c.4-.3-.1-.5-.6-.2L6.4 13.3l-4.8-1.5c-1-.3-1-1 .2-1.5L20.6 3c.9-.3 1.6.2 1.3 1.3z" />
  </svg>
);

/* ── Toast ────────────────────────────────────────────── */

function Toast({ message }: { message: string }) {
  return <div className="ob-toast">{message}</div>;
}

/* ── Step 1 — Welcome ─────────────────────────────────── */

function Step1({ t }: { t: (k: string) => string }) {
  return (
    <div className="ob-step">
      <div className="ob-welcome-head">
        <div className="sun-badge" aria-hidden="true">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="8" fill="white" fillOpacity="0.9" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
              <rect
                key={deg}
                x="17"
                y="3"
                width="2"
                height="5"
                rx="1"
                fill="white"
                fillOpacity="0.85"
                transform={`rotate(${deg} 18 18)`}
              />
            ))}
          </svg>
        </div>
        <h1>{t("onboarding.step1.title")}</h1>
        <p>{t("onboarding.step1.subtitle")}</p>
        <span className="ob-pill"><ClockIcon size={14} /> {t("onboarding.step1.pill")}</span>
      </div>

      <div className="ob-feature-cards">
        <div className="card ob-feature-card">
          <div className="ob-feature-icon"><EuroIcon /></div>
          <div>
            <h3>{t("onboarding.step1.feature1Title")}</h3>
            <p>{t("onboarding.step1.feature1Desc")}</p>
          </div>
        </div>
        <div className="card ob-feature-card">
          <div className="ob-feature-icon"><ClockIcon /></div>
          <div>
            <h3>{t("onboarding.step1.feature2Title")}</h3>
            <p>{t("onboarding.step1.feature2Desc")}</p>
          </div>
        </div>
        <div className="card ob-feature-card">
          <div className="ob-feature-icon"><PlaneIcon /></div>
          <div>
            <h3>{t("onboarding.step1.feature3Title")}</h3>
            <p>{t("onboarding.step1.feature3Desc")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Step 2 — TJM ────────────────────────────────────── */

const CHIPS = [350, 450, 550, 650];

function Step2({
  t,
  tjm,
  setTjm,
}: {
  t: (k: string, opts?: Record<string, unknown>) => string;
  tjm: number;
  setTjm: (v: number) => void;
}) {
  const monthlyEst = tjm * 20;

  return (
    <div className="ob-step">
      <div className="ob-step-head">
        <h2>{t("onboarding.step2.title")}</h2>
        <p>{t("onboarding.step2.subtitle")}</p>
      </div>

      <div className="field">
        <label className="field-label">{t("onboarding.step2.label")}</label>
        <div className="ob-tjm-input-wrap">
          <span className="ob-tjm-prefix">€</span>
          <input
            type="number"
            className="ob-tjm-input"
            value={tjm || ""}
            min={0}
            max={9999}
            placeholder={t("onboarding.step2.placeholder")}
            onChange={(e) => setTjm(Math.max(0, Number(e.target.value)))}
            aria-label={t("onboarding.step2.label")}
          />
        </div>
      </div>

      <div className="ob-chips">
        {CHIPS.map((v) => (
          <button
            key={v}
            type="button"
            className={`ob-chip${tjm === v ? " selected" : ""}`}
            onClick={() => setTjm(v)}
          >
            {v} €
          </button>
        ))}
      </div>

      {tjm > 0 && (
        <div className="card ob-glance">
          <div>
            <div className="field-label">{t("onboarding.step2.glanceTitle")}</div>
            <div className="ob-glance-amount">{t("onboarding.step2.glanceMonth", { amount: formatEuro(monthlyEst * 100) })}</div>
            <div className="ob-glance-note">{t("onboarding.step2.glanceNote")}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Step 3 — Daily ping ─────────────────────────────── */

function Step3({
  t,
  pingFreq,
  setPingFreq,
  pingH,
  setPingH,
  pingM,
  setPingM,
}: {
  t: (k: string, opts?: Record<string, unknown>) => string;
  pingFreq: "weekdays" | "daily";
  setPingFreq: (v: "weekdays" | "daily") => void;
  pingH: number;
  setPingH: (v: number) => void;
  pingM: number;
  setPingM: (v: number) => void;
}) {
  const previewFreq =
    pingFreq === "weekdays" ? t("onboarding.step3.weekdays") : t("onboarding.step3.everyday");
  const previewTime = `${pad(pingH)}:${pad(pingM)}`;

  return (
    <div className="ob-step">
      <div className="ob-step-head">
        <h2>{t("onboarding.step3.title")}</h2>
        <p>{t("onboarding.step3.subtitle")}</p>
      </div>

      <div className="field">
        <span className="field-label">{t("onboarding.step3.freqLabel")}</span>
        <div className="ob-segmented" role="group" aria-label={t("onboarding.step3.freqLabel")}>
          <button
            type="button"
            className={`ob-seg-btn${pingFreq === "weekdays" ? " active" : ""}`}
            onClick={() => setPingFreq("weekdays")}
          >
            {t("onboarding.step3.weekdays")}
          </button>
          <button
            type="button"
            className={`ob-seg-btn${pingFreq === "daily" ? " active" : ""}`}
            onClick={() => setPingFreq("daily")}
          >
            {t("onboarding.step3.everyday")}
          </button>
        </div>
      </div>

      <div className="ob-time-row">
        <div className="field">
          <label className="field-label">{t("onboarding.step3.hourLabel")}</label>
          <select
            className="input"
            value={pingH}
            onChange={(e) => setPingH(Number(e.target.value))}
            aria-label={t("onboarding.step3.hourLabel")}
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>
                {pad(i)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label">{t("onboarding.step3.minuteLabel")}</label>
          <select
            className="input"
            value={pingM}
            onChange={(e) => setPingM(Number(e.target.value))}
            aria-label={t("onboarding.step3.minuteLabel")}
          >
            {[0, 15, 30, 45].map((m) => (
              <option key={m} value={m}>
                {pad(m)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="ob-preview">
        {t("onboarding.step3.preview", { freq: previewFreq, time: previewTime })}
      </div>
    </div>
  );
}

/* ── Step 4 — Payment reminder ───────────────────────── */

function Step4({
  t,
  payDay,
  setPayDay,
  payH,
  setPayH,
  payM,
  setPayM,
}: {
  t: (k: string, opts?: Record<string, unknown>) => string;
  payDay: number | "last";
  setPayDay: (v: number | "last") => void;
  payH: number;
  setPayH: (v: number) => void;
  payM: number;
  setPayM: (v: number) => void;
}) {
  const dayLabel = payDay === "last" ? t("onboarding.step4.lastDay") : String(payDay);
  const previewTime = `${pad(payH)}:${pad(payM)}`;

  return (
    <div className="ob-step">
      <div className="ob-step-head">
        <h2>{t("onboarding.step4.title")}</h2>
        <p>{t("onboarding.step4.subtitle")}</p>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="ob-pay-day">{t("onboarding.step4.dayLabel")}</label>
        <select
          id="ob-pay-day"
          className="input"
          value={payDay === "last" ? "last" : String(payDay)}
          onChange={(e) => setPayDay(e.target.value === "last" ? "last" : Number(e.target.value))}
        >
          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
          <option value="last">{t("onboarding.step4.lastDay")}</option>
        </select>
      </div>

      <div className="ob-time-row">
        <div className="field">
          <label className="field-label">{t("onboarding.step4.hourLabel")}</label>
          <select
            className="input"
            value={payH}
            onChange={(e) => setPayH(Number(e.target.value))}
            aria-label={t("onboarding.step4.hourLabel")}
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>
                {pad(i)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label">{t("onboarding.step4.minuteLabel")}</label>
          <select
            className="input"
            value={payM}
            onChange={(e) => setPayM(Number(e.target.value))}
            aria-label={t("onboarding.step4.minuteLabel")}
          >
            {[0, 15, 30, 45].map((m) => (
              <option key={m} value={m}>
                {pad(m)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="ob-preview">
        {t("onboarding.step4.preview", { day: dayLabel, time: previewTime })}
      </div>
    </div>
  );
}

/* ── Step 5 — Telegram ───────────────────────────────── */

function Step5({
  t,
  code,
  botUsername,
  connected,
  onCopy,
}: {
  t: (k: string) => string;
  code: string | null;
  botUsername: string | null;
  connected: boolean;
  onCopy: (text: string) => void;
}) {
  const command = code ? `/start ${code}` : "/start …";
  const deepLink = botUsername && code ? `https://t.me/${botUsername}?start=${code}` : null;

  return (
    <div className="ob-step">
      <div className="ob-step-head">
        <h2>{t("onboarding.step5.title")}</h2>
        <p>{t("onboarding.step5.subtitle")}</p>
      </div>

      {botUsername && (
        <div className="field">
          <span className="field-label">{t("onboarding.step5.botHandleLabel")}</span>
          <div className="ob-tg-code-box">
            <div className="ob-tg-code-row">
              <span className="ob-tg-code">@{botUsername}</span>
              <button
                type="button"
                className="ob-copy-btn icon"
                onClick={() => onCopy(`@${botUsername}`)}
                aria-label="Copy bot handle"
              >
                <CopyIcon size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="field">
        <span className="field-label">{t("onboarding.step5.commandLabel")}</span>
        <div className="ob-tg-code-box dark">
          <div className="ob-tg-code-row">
            <span className="ob-tg-code">{command}</span>
            <button
              type="button"
              className="ob-copy-btn"
              onClick={() => onCopy(command)}
              aria-label={t("onboarding.step5.copyCommand")}
            >
              <CopyIcon size={15} /> {t("onboarding.step5.copyCommand")}
            </button>
          </div>
        </div>
      </div>

      {deepLink && (
        <a
          href={deepLink}
          target="_blank"
          rel="noopener noreferrer"
          className="btn primary"
          style={{ textDecoration: "none" }}
        >
          <PlaneIcon size={16} /> {t("onboarding.step5.openTelegram")}
        </a>
      )}

      <div style={{ display: "flex", justifyContent: "center" }}>
        <span className={`ob-tg-status ${connected ? "connected" : "waiting"}`}>
          {connected ? <CheckIcon size={15} /> : <span className="ob-tg-dot" />}
          {connected ? t("onboarding.step5.statusConnected") : t("onboarding.step5.statusWaiting")}
        </span>
      </div>

      <p className="ob-tg-skip-hint">{t("onboarding.step5.skipHint")}</p>
    </div>
  );
}

/* ── Step 6 — Done ───────────────────────────────────── */

function Step6({
  t,
  tjm,
  pingFreq,
  pingH,
  pingM,
  payDay,
  payH,
  payM,
  telegramConnected,
}: {
  t: (k: string, opts?: Record<string, unknown>) => string;
  tjm: number;
  pingFreq: "weekdays" | "daily";
  pingH: number;
  pingM: number;
  payDay: number | "last";
  payH: number;
  payM: number;
  telegramConnected: boolean;
}) {
  const freqLabel =
    pingFreq === "weekdays" ? t("onboarding.step3.weekdays") : t("onboarding.step3.everyday");
  const dayLabel =
    payDay === "last" ? t("onboarding.step4.lastDay") : String(payDay);

  return (
    <div className="ob-step">
      <div className="ob-done-head">
        <div className="ob-done-check"><CheckIcon size={38} /></div>
        <h2>{t("onboarding.step6.title")}</h2>
        <p>{t("onboarding.step6.subtitle")}</p>
      </div>

      <div className="ob-recap">
        <div className="ob-recap-row">
          <span className="ob-recap-left">
            <span className="ob-recap-icon"><EuroIcon size={15} /></span>
            <span className="ob-recap-label">{t("onboarding.step6.rate")}</span>
          </span>
          <span className="ob-recap-value">{tjm > 0 ? `${tjm} €/jour` : "—"}</span>
        </div>
        <div className="ob-recap-row">
          <span className="ob-recap-left">
            <span className="ob-recap-icon"><ClockIcon size={15} /></span>
            <span className="ob-recap-label">{t("onboarding.step6.dailyReminder")}</span>
          </span>
          <span className="ob-recap-value">
            {freqLabel} — {pad(pingH)}:{pad(pingM)}
          </span>
        </div>
        <div className="ob-recap-row">
          <span className="ob-recap-left">
            <span className="ob-recap-icon"><CalendarIcon size={15} /></span>
            <span className="ob-recap-label">{t("onboarding.step6.paymentReminder")}</span>
          </span>
          <span className="ob-recap-value">
            {t("onboarding.step4.preview", { day: dayLabel, time: `${pad(payH)}:${pad(payM)}` })}
          </span>
        </div>
        <div className="ob-recap-row">
          <span className="ob-recap-left">
            <span className="ob-recap-icon"><PlaneIcon size={15} /></span>
            <span className="ob-recap-label">{t("onboarding.step6.telegram")}</span>
          </span>
          <span className="ob-recap-value">
            {telegramConnected
              ? t("onboarding.step6.telegramConnected")
              : t("onboarding.step6.telegramSkipped")}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Main Onboarding component ───────────────────────── */

const TOTAL_STEPS = 6;

export function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const upsertSchedule = useUpsertSchedule();
  const generateCode = useGenerateTelegramCode();

  // Step state
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState<string | null>(null);

  // Step 2 — TJM
  const [tjm, setTjm] = useState(0);

  // Step 3 — daily ping
  const [pingFreq, setPingFreq] = useState<"weekdays" | "daily">("weekdays");
  const [pingH, setPingH] = useState(19);
  const [pingM, setPingM] = useState(0);

  // Step 4 — payment reminder
  const [payDay, setPayDay] = useState<number | "last">(1);
  const [payH, setPayH] = useState(10);
  const [payM, setPayM] = useState(0);

  // Step 5 — Telegram
  const [tgCode, setTgCode] = useState<string | null>(null);
  const [tgConnectedState, setTgConnectedState] = useState(false);

  const telegramStatus = useTelegramStatus({
    refetchInterval: step === 5 && !tgConnectedState ? 2500 : false,
  });

  // Pre-fill from existing settings
  useEffect(() => {
    if (!settings.data) return;
    const s = settings.data;
    if (s.tjmCents > 0) setTjm(Math.round(s.tjmCents / 100));
    if (s.dailyPingTime) {
      const { h, m } = parseTime(s.dailyPingTime);
      setPingH(h);
      setPingM(m);
    }
    if (s.paymentReminderDay) setPayDay(s.paymentReminderDay);
    if (s.paymentReminderTime) {
      const { h, m } = parseTime(s.paymentReminderTime);
      setPayH(h);
      setPayM(m);
    }
  }, [settings.data]);

  // When entering step 5, generate a telegram code
  useEffect(() => {
    if (step === 5 && !tgCode) {
      generateCode
        .mutateAsync()
        .then((res) => setTgCode(res.code))
        .catch(() => {});
    }
  }, [step]);

  const showToast = useCallback(
    (msg: string) => {
      setToast(msg);
      setTimeout(() => setToast(null), 2000);
    },
    [],
  );

  const handleCopy = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text).catch(() => {});
      showToast(t("onboarding.copied"));
    },
    [t, showToast],
  );

  const persistStep = useCallback(
    async (currentStep: number) => {
      try {
        if (currentStep === 2) {
          await updateSettings.mutateAsync({ tjmCents: tjm * 100 });
        } else if (currentStep === 3) {
          const cron = `${pingM} ${pingH} * * ${pingFreq === "daily" ? "*" : "1-5"}`;
          const time = `${pad(pingH)}:${pad(pingM)}`;
          await Promise.all([
            updateSettings.mutateAsync({
              dailyPingTime: time,
              dailyPingTimes: time,
              dailyPingEveryday: pingFreq === "daily",
            }),
            upsertSchedule.mutateAsync({ kind: "daily_ping", cron, enabled: true }),
          ]);
        } else if (currentStep === 4) {
          const dayVal = payDay === "last" ? 28 : payDay;
          const cron = `${payM} ${payH} ${dayVal} * *`;
          await Promise.all([
            updateSettings.mutateAsync({
              paymentReminderDay: dayVal,
              paymentReminderTime: `${pad(payH)}:${pad(payM)}`,
            }),
            upsertSchedule.mutateAsync({ kind: "payment_reminder", cron, enabled: true }),
          ]);
        }
      } catch {
        // best-effort; don't block navigation
      }
    },
    [tjm, pingFreq, pingH, pingM, payDay, payH, payM, updateSettings, upsertSchedule],
  );

  const handleNext = useCallback(async () => {
    await persistStep(step);
    if (step === TOTAL_STEPS) {
      try {
        await updateSettings.mutateAsync({ onboarded: true });
      } catch {
        // best-effort
      }
      void navigate("/");
      return;
    }
    setStep((s) => s + 1);
  }, [step, persistStep, updateSettings, navigate]);

  const handleBack = useCallback(() => {
    setStep((s) => Math.max(1, s - 1));
  }, []);

  const handleSkip = useCallback(async () => {
    try {
      await updateSettings.mutateAsync({ onboarded: true });
    } catch {
      // best-effort
    }
    void navigate("/");
  }, [updateSettings, navigate]);

  const tgConnected = telegramStatus.data?.connected ?? false;
  const botUsername = telegramStatus.data?.botUsername ?? null;

  // Keep local state in sync with polled status
  useEffect(() => {
    if (tgConnected) setTgConnectedState(true);
  }, [tgConnected]);

  const primaryLabel =
    step === 1
      ? t("onboarding.getStarted")
      : step === TOTAL_STEPS
        ? t("onboarding.finish")
        : t("onboarding.next");

  return (
    <div className="ob-wrap">
      <div className="ob-inner">
        {/* Progress */}
        <div className="ob-progress" role="progressbar" aria-label={t("onboarding.progressLabel")}>
          <div className="ob-progress__bar">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => (
              <div key={i} className={`ob-progress__seg${i < step ? " active" : ""}`} />
            ))}
          </div>
          <span className="ob-progress__label">
            {t("onboarding.stepOf", { current: step, total: TOTAL_STEPS })}
          </span>
          {step < TOTAL_STEPS && (
            <button type="button" className="ob-skip" onClick={() => void handleSkip()}>
              {t("onboarding.skip")}
            </button>
          )}
        </div>

        {/* Steps */}
        {step === 1 && <Step1 t={t} />}
        {step === 2 && <Step2 t={t} tjm={tjm} setTjm={setTjm} />}
        {step === 3 && (
          <Step3
            t={t}
            pingFreq={pingFreq}
            setPingFreq={setPingFreq}
            pingH={pingH}
            setPingH={setPingH}
            pingM={pingM}
            setPingM={setPingM}
          />
        )}
        {step === 4 && (
          <Step4
            t={t}
            payDay={payDay}
            setPayDay={setPayDay}
            payH={payH}
            setPayH={setPayH}
            payM={payM}
            setPayM={setPayM}
          />
        )}
        {step === 5 && (
          <Step5
            t={t}
            code={tgCode}
            botUsername={botUsername}
            connected={tgConnected}
            onCopy={handleCopy}
          />
        )}
        {step === 6 && (
          <Step6
            t={t}
            tjm={tjm}
            pingFreq={pingFreq}
            pingH={pingH}
            pingM={pingM}
            payDay={payDay}
            payH={payH}
            payM={payM}
            telegramConnected={tgConnected}
          />
        )}

        {/* Spacer so footer doesn't overlap last card */}
        <div style={{ flex: 1, minHeight: 24 }} />
      </div>

      {/* Footer */}
      <div className="ob-footer">
        {step > 1 && (
          <button type="button" className="btn secondary" onClick={handleBack}>
            {t("onboarding.back")}
          </button>
        )}
        <button
          type="button"
          className="btn primary"
          onClick={() => void handleNext()}
          style={step === 1 ? { flex: 1 } : {}}
        >
          {primaryLabel}
        </button>
      </div>

      {toast && <Toast message={toast} />}
    </div>
  );
}
